import * as yaml from 'js-yaml';
import { ToolRevisionData, ToolParameterSchema } from '../store/interfaces/Tool.types';

export interface YamlExportNotification {
  type: 'success' | 'error';
  content: string;
  id: string;
}

interface YamlToolSchema {
  tool_name: string;
  description: string;
  input_schema?: Record<string, ToolParameterSchema>;
  required?: string[];
}

/**
 * Build YAML string directly from revision data (combined helper)
 */
function buildYamlFromRevisionData(revisionData: ToolRevisionData, toolId: string): string {
  let yamlLines: string[] = [];
  
  // Tool name
  yamlLines.push(`tool_name: "${revisionData.toolName || toolId}"`);
  
  // Description (handle multiline with | if needed)
  const description = revisionData.description || '';
  if (description.includes('\n')) {
    yamlLines.push('description: |');
    description.split('\n').forEach(line => {
      yamlLines.push(`  ${line}`);
    });
  } else {
    yamlLines.push(`description: "${description}"`);
  }
  
  // Input schema (only add if there are parameters)
  if (revisionData.inputSchema && Object.keys(revisionData.inputSchema).length > 0) {
    yamlLines.push('input_schema:');
    
    Object.entries(revisionData.inputSchema as Record<string, ToolParameterSchema>).forEach(([paramKey, param]) => {
      yamlLines.push(`  ${paramKey}:`);
      yamlLines.push(`    parameterName: "${paramKey}"`);
      yamlLines.push(`    type: "${param.type}"`);
      
      if (param.items) {
        yamlLines.push('    items:');
        yamlLines.push(`      type: "${param.items.type}"`);
      }
      
      if (param.description) {
        if (param.description.includes('\n')) {
          yamlLines.push('    description: |');
          param.description.split('\n').forEach(line => {
            yamlLines.push(`      ${line}`);
          });
        } else {
          yamlLines.push(`    description: "${param.description}"`);
        }
      }
      
      yamlLines.push(`    required: ${param.required || false}`);
      
      if (param.default !== undefined && param.default !== null) {
        if (Array.isArray(param.default)) {
          yamlLines.push(`    default: [${param.default.map(v => JSON.stringify(v)).join(', ')}]`);
        } else {
          yamlLines.push(`    default: ${JSON.stringify(param.default)}`);
        }
      }
      
      if (param.enum && param.enum.length > 0) {
        yamlLines.push(`    enum: [${param.enum.map(v => JSON.stringify(v)).join(', ')}]`);
      }
      
      if (param.examples !== undefined && param.examples !== null) {
        const examples = Array.isArray(param.examples) ? param.examples : [param.examples];
        const formattedExamples = examples.map(example => {
          if (Array.isArray(example)) {
            return `[${example.map(v => JSON.stringify(v)).join(', ')}]`;
          }
          return JSON.stringify(example);
        });
        yamlLines.push(`    examples: [${formattedExamples.join(', ')}]`);
      }
    });
  }
  
  // Required parameters at root level
  if (revisionData.required && Array.isArray(revisionData.required)) {
    yamlLines.push(`required: [${revisionData.required.map((r: string) => JSON.stringify(r)).join(', ')}]`);
  }
  
  return yamlLines.join('\n');
}

/**
 * Downloads tool schema as YAML file in the specified format
 * @param revisionData - The tool revision data to export
 * @param toolId - The tool ID for fallback naming
 * @param onNotification - Optional callback for success/error notifications
 */
export const downloadToolSchemaAsYaml = (
  revisionData: ToolRevisionData,
  toolId: string,
  onNotification?: (notification: YamlExportNotification) => void
): void => {
  try {
    const yamlContent = buildYamlFromRevisionData(revisionData, toolId);

    // Create and download the file
    const blob = new Blob([yamlContent], { type: 'application/x-yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${revisionData.toolName || toolId}_v${revisionData.version}_r${revisionData.revision}_schema.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Success notification
    if (onNotification) {
      onNotification({
        type: 'success',
        content: 'Tool schema downloaded successfully',
        id: 'schema-download-success'
      });
    }

  } catch (error) {
    console.error('Error downloading schema as YAML:', error);
    
    // Error notification
    if (onNotification) {
      onNotification({
        type: 'error',
        content: 'Error downloading schema as YAML',
        id: 'schema-download-error'
      });
    }
  }
};

/**
 * Preview the YAML content without downloading (useful for debugging)
 * @param revisionData - The tool revision data to preview
 * @param toolId - The tool ID for fallback naming
 * @returns The YAML content as a string
 */
export const previewToolSchemaAsYaml = (
  revisionData: ToolRevisionData,
  toolId: string
): string => {
  return buildYamlFromRevisionData(revisionData, toolId);
};
